// Right panel: code table + search + code CRUD entry points (PRD 08 §2.2 #6-#10).
// Form panel and delete confirm are mounted here because they only operate on
// code-scoped state (and need the active group id).

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MousePointerClick, NotepadText, Plus, Search, SearchX } from 'lucide-react';

import { useCan } from '@/features/roles/hooks/use-can';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';

import { useDiseaseCodesQuery } from '../hooks/use-disease-codes';
import { useDiseaseGroupsQuery } from '../hooks/use-disease-groups';
import { useDiseaseCodePageStore } from '../store/disease-code-page.store';
import type { DiseaseCode } from '../types/disease-code.types';
import { DeleteDiseaseCodeConfirm } from './DeleteDiseaseCodeConfirm';
import { DiseaseCodeFormPanel } from './DiseaseCodeFormPanel';
import { DiseaseCodeReferencePanel } from './DiseaseCodeReferencePanel';
import { DiseaseCodeTable } from './DiseaseCodeTable';
import { useWriteDenyGuard } from '@/shared/hooks/useWriteGuard';

// PRD 08 §3.3 — 300ms debounce on the real-time search.
const SEARCH_DEBOUNCE_MS = 300;

function CodeTableSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="animate-pulse"
          style={{ height: 36, background: 'var(--muted)', borderRadius: 'var(--radius-sm)' }}
        />
      ))}
    </div>
  );
}

function CodeTableError({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation('admin');
  return (
    <div
      role="alert"
      style={{
        padding: 16,
        border: '1px solid var(--destructive)',
        borderRadius: 'var(--radius-md)',
        background: '#FEE2E8',
        color: 'var(--destructive)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
      }}
    >
      <span>{message}</span>
      <button type="button" className="btn btn-sm btn-outline" onClick={onRetry}>
        {t('adminManagement.actions.retry')}
      </button>
    </div>
  );
}

function CenteredHint({
  icon,
  message,
}: {
  icon: React.ReactNode;
  message: string;
}) {
  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--muted-foreground)',
        gap: 8,
        padding: 24,
        textAlign: 'center',
      }}
    >
      {icon}
      <p style={{ margin: 0 }}>{message}</p>
    </div>
  );
}

export function DiseaseCodePanel() {
  const { t } = useTranslation('admin');

  const canWrite = useCan('METADATA', 'WRITE');

  const selectedGroupId = useDiseaseCodePageStore((s) => s.selectedGroupId);
  const selectedCodeId = useDiseaseCodePageStore((s) => s.selectedCodeId);
  const selectCode = useDiseaseCodePageStore((s) => s.selectCode);
  const search = useDiseaseCodePageStore((s) => s.search);
  const setSearch = useDiseaseCodePageStore((s) => s.setSearch);

  const { data: groups } = useDiseaseGroupsQuery();
  const { data: codes, isLoading, error, refetch } = useDiseaseCodesQuery(selectedGroupId);

  const selectedGroup = useMemo(
    () => groups?.find((g) => g.id === selectedGroupId) ?? null,
    [groups, selectedGroupId],
  );
  const selectedCode = useMemo(
    () => codes?.find((c) => c.id === selectedCodeId) ?? null,
    [codes, selectedCodeId],
  );

  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  // PRD §3.3 — real-time match on code + name + description, case-insensitive.
  // Prototype concatenates with a single space and uses indexOf > -1.
  const filteredCodes = useMemo(() => {
    if (!codes) return [];
    const needle = debouncedSearch.trim().toLowerCase();
    if (!needle) return codes;
    return codes.filter((c) => {
      const haystack = `${c.code} ${c.name} ${c.description ?? ''}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [codes, debouncedSearch]);

  const hasGroup = selectedGroupId !== null;

  // Form open / target state mirrors the group panel: held separately so the
  // slide-out animation completes before the target resets to null.
  const [formOpen, setFormOpen] = useState(false);
  const [formTarget, setFormTarget] = useState<DiseaseCode | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DiseaseCode | null>(null);
  // SHCT-582 (QC re-test) — a real `disabled` rather than `aria-disabled`: §7.4 P-2 is 후속 동작
  // 없음, and only a real one is visible to the L2 guard that answers a bypass (P-3). The button
  // already carried a structural `disabled` for "no group selected"; permission joins it.
  const { denyProps } = useWriteDenyGuard(canWrite);

  const openCreateCode = () => {
    setFormTarget(null);
    setFormOpen(true);
  };
  const openEditCode = (code: DiseaseCode) => {
    setFormTarget(code);
    setFormOpen(true);
  };
  const closeForm = () => {
    setFormOpen(false);
    window.setTimeout(() => setFormTarget(null), 300);
  };

  return (
    <>
      <div
        className="card"
        style={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          flex: 1,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 16px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
            }}
          >
            <NotepadText size={16} />
            <span>
              {selectedGroup
                ? t('diseaseCodeManagement.codePanel.titleGroup', { name: selectedGroup.name })
                : t('diseaseCodeManagement.codePanel.titleNoGroup')}
            </span>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={!hasGroup || !canWrite}
            onClick={openCreateCode}
            {...denyProps}
          >
            <Plus size={14} />
            {t('diseaseCodeManagement.codePanel.add')}
          </button>
        </div>

        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            gap: 8,
            alignItems: 'center',
          }}
        >
          <div style={{ position: 'relative', flex: 1 }}>
            <Search
              size={14}
              style={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--muted-foreground)',
                pointerEvents: 'none',
              }}
            />
            <input
              className="form-field__input"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('diseaseCodeManagement.codePanel.searchPlaceholder')}
              disabled={!hasGroup}
              style={{ paddingLeft: 32, margin: 0 }}
            />
          </div>
        </div>

        <div
          style={{
            padding: '12px 16px',
            overflow: 'auto',
            flex: 1,
          }}
        >
          {!hasGroup ? (
            <CenteredHint
              icon={<MousePointerClick size={44} />}
              message={t('diseaseCodeManagement.codePanel.emptyNoGroup')}
            />
          ) : isLoading ? (
            <CodeTableSkeleton />
          ) : error ? (
            <CodeTableError
              message={t('diseaseCodeManagement.toasts.loadCodesError')}
              onRetry={() => void refetch()}
            />
          ) : filteredCodes.length === 0 ? (
            <CenteredHint
              icon={<SearchX size={44} />}
              message={t('diseaseCodeManagement.codePanel.emptyNoMatch')}
            />
          ) : (
            <DiseaseCodeTable
              codes={filteredCodes}
              selectedCodeId={selectedCodeId}
              onRowSelect={selectCode}
              onEdit={openEditCode}
              onDelete={setDeleteTarget}
              canWrite={canWrite}
            />
          )}
        </div>
        {selectedCode ? <DiseaseCodeReferencePanel code={selectedCode} /> : null}
      </div>

      {selectedGroupId !== null ? (
        <DiseaseCodeFormPanel
          open={formOpen}
          groupId={selectedGroupId}
          rangeStart={selectedGroup?.rangeStart ?? ''}
          rangeEnd={selectedGroup?.rangeEnd ?? ''}
          code={formTarget}
          onClose={closeForm}
        />
      ) : null}
      <DeleteDiseaseCodeConfirm code={deleteTarget} onClose={() => setDeleteTarget(null)} />
    </>
  );
}
